import { useId, type ReactNode } from 'react';

/** A control the approved design shows but that has no real implementation yet (EPN-236: Email, Export/Download).
 *
 * It is never faked. It stays keyboard-focusable (`aria-disabled`, not the `disabled` attribute, which would
 * remove it from the tab order and hide the explanation from keyboard and screen-reader users), does nothing
 * when activated, and always exposes the reason as an accessible name suffix, a description and a tooltip. */
export function UnavailableAction({
  label,
  reason,
  className,
  children,
}: {
  /** Accessible name of the action, e.g. "Email payslip for Morgan Example". */
  label: string;
  /** Plain-language reason, e.g. "Email delivery is not available yet". Also shown as the tooltip. */
  reason: string;
  className?: string;
  children: ReactNode;
}) {
  const reasonId = useId();
  return (
    <span className="payslips__unavailable">
      <button
        type="button"
        className={className}
        aria-disabled="true"
        aria-label={`${label} (not available yet)`}
        aria-describedby={reasonId}
        title={reason}
        onClick={(event) => event.preventDefault()}
      >
        {children}
      </button>
      <span role="tooltip" id={reasonId} className="payslips__tooltip">{reason}</span>
    </span>
  );
}

export function EyeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <circle cx="8" cy="8" r="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

export function MailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="1.8" y="3.2" width="12.4" height="9.6" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2.4 4.4L8 9l5.6-4.6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

export function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M8 2.5v7.2M4.8 6.8L8 10l3.2-3.2M3 13h10" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function PrintIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path d="M4.5 6V2.5h7V6M4.5 11.5h-2v-5h11v5h-2M4.5 9.5h7v4h-7z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

export function FolderIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
      <path d="M4 10a2 2 0 0 1 2-2h9l3 4h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" fill="currentColor" />
    </svg>
  );
}
